<template>
    <div
        v-if="categories"
        class="blog-menu"
    >
        <button
            class="toggle"
            type="button"
            @click="toggleCategories"
        >
            Categories
        </button>

        <ul
            :class="classCategories"
            class="categories"
        >
            <li
                v-for="category in categories"
                :key="category.id"
                :class="getClassItem(category)"
                class="item"
            >
                <ThLink
                    :to="routes.blog.category(category)"
                    class="link"
                >
                    {{ category.name }}
                </ThLink>

                <ul
                    v-if="category.children?.length"
                    class="children"
                >
                    <li
                        v-for="child in category.children"
                        :key="child.id"
                        class="item"
                    >
                        <ThLink
                            :to="routes.blog.category(child)"
                            class="link"
                        >
                            {{ child.name }}
                        </ThLink>
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import type { BlogMenuFragment } from '@/graphql/api'
import { useRoutes } from '@/router/routes'

import ThLink from '@/components/ui/buttons/ThLink.vue'

defineProps<{
    categories: BlogMenuFragment[]
}>()

const routes = useRoutes()

const isOpen = ref(false)

const toggleCategories = () => {
    isOpen.value = !isOpen.value
}

const classCategories = computed(() => ({
    'open': isOpen.value,
}))

const getClassItem = (category: BlogMenuFragment) => ({
    'with-submenu': category.children?.length,
})
</script>

<style lang="scss" scoped>
.blog-menu {
    width: 1140px;
    max-width: 100%;
    margin: 30px auto 0;
    padding: 0;
    font-family: $effra-font-stack;
    font-size: 0.875rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 400;

    @include min-width(1024) {
        display: flex;
        justify-content: space-between;
    }

    .toggle {
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include border-bottom;

        width: 100%;
        padding: 0 0 10px;
        margin: 0 0 10px;

        @include min-width(1024) {
            display: none;
        }

        &::after {
            font-family: $icon-font;
            content: $icon-hamburger;
        }
    }

    .categories {
        display: none;

        &.open {
            display: block;

            @include min-width(1024) {
                display: flex;
            }
        }

        @include min-width(1024) {
            display: flex;
            align-items: center;
            justify-content: flex-start;
        }

        .item {
            margin: 0 30px 0 0;
            position: relative;

            &.with-submenu {
                > .link {
                    &::after {
                        font-family: $icon-font;
                        content: $icon-angle-down;
                        font-size: 0.75rem;
                        font-weight: 900;
                        margin-left: 4px;
                    }
                }
            }

            .link {
                display: block;
                color: $slate;
                padding-bottom: 10px;
            }

            .children {
                padding: 0 0 10px;

                @include min-width(1024) {
                    display: none;
                    background: $white;
                    box-shadow: 0 0 20px rgba($black, 0.1);
                    width: 240px;
                    margin-left: -10px;
                    padding: 10px 20px;
                    position: absolute;
                }

                .item {
                    width: 100%;
                    padding: 0 0 0 20px;

                    @include min-width(1024) {
                        padding: 0;
                    }

                    .link {
                        padding: 5px 0;
                        display: block;
                    }
                }
            }

            @include hover() {
                .children {
                    display: block;
                }
            }
        }
    }
}
</style>
